<script setup lang="ts">
import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { LineChartSpec } from "../sbi/protocol";
import { LINE_STROKE_WIDTH } from "../theme.ts";
import { baseOption } from "./echarts-base";
import { autoYDomainFor } from "./chart-yscale.ts";
import { categoryLabelPolicy, dataZoomOption, plotWidthEstimate } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch — drives margins, legend,
// tick and label policy (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: LineChartSpec;
    /**
     * Field → format registry from `sbi.format(...)`. When `spec.yFormat`
     * isn't set, the y-axis falls back to the registered format for the
     * first y field — single-y charts get a clean fallback; multi-y charts
     * with heterogeneous formats should still set `yFormat` explicitly.
     */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const yFields = computed(() =>
  Array.isArray(props.spec.y) ? props.spec.y : [props.spec.y],
);

// `spec.data` is a `ChartData` union (rows-array | SqlMarker | DeriveMarker).
// InsightChart only dispatches to this subcomponent after the marker has
// resolved, so the runtime value is always a rows array — narrow with
// `Array.isArray` so the rest of the component can index/map without
// repeated unions, and so a defective dispatch path can't crash render.
const rows = computed(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

// Axis-format fallback chain: per-axis `spec.yFormat` → per-call
// `spec.format` (DefaultOpts) → registry format for the first y field.
const resolvedYFormat = computed(
  () =>
    props.spec.yFormat ??
    props.spec.format ??
    props.formats[yFields.value[0]],
);

const option = computed(() => {
  const base = baseOption({
    title: props.spec.title,
    yFormat: resolvedYFormat.value,
    legendNames: yFields.value.length > 1 ? yFields.value : [],
  }, ctx.value);
  // The slider variant of dataZoom paints a brush strip that, left at
  // ECharts' default `bottom: 0`, lands on top of the x-axis labels.
  // `dataZoomOption` anchors it at the chart floor and reports the room
  // it needs, which the grid's bottom margin adds. On touch screens it
  // swaps `inside` for a slider (inside zoom traps page scrolling);
  // print drops it.
  const zoom = dataZoomOption(props.spec.dataZoom, ctx.value);
  const xLabels = rows.value.map((row) => row[props.spec.x]);
  return {
    ...base,
    grid: { ...base.grid, bottom: base.grid.bottom + zoom.reserveBottom },
    xAxis: {
      ...base.xAxis,
      type: "category",
      data: xLabels,
      axisLabel: {
        ...base.xAxis.axisLabel,
        ...categoryLabelPolicy({
          labels: xLabels,
          plotWidth: plotWidthEstimate(ctx.value.width),
          mode: "thin",
        }),
      },
    },
    // Auto-scale walks every plotted y value across every y-field; the
    // helper returns `undefined` when the data has no range, leaving
    // ECharts to pick a default "nice" domain.
    yAxis: {
      ...base.yAxis,
      type: "value" as const,
      ...(autoYDomainFor(
        props.spec.yScale,
        (function* () {
          for (const row of rows.value) {
            for (const field of yFields.value) yield row[field];
          }
        })(),
      ) ?? {}),
    },
    series: yFields.value.map((field) => ({
      name: field,
      type: "line",
      // Gentle smoothing reads more polished than ECharts' default
      // sharp polyline; 0.4 stays close enough to the data shape that
      // it doesn't lie about values between points.
      smooth: 0.4,
      lineStyle: { width: LINE_STROKE_WIDTH },
      // Per-point dots add visual clutter on dense series; emphasis on
      // hover still surfaces them via ECharts' built-in symbol-on-hover.
      showSymbol: false,
      symbol: "circle",
      data: rows.value.map((row) => row[field]),
    })),
    legend: yFields.value.length > 1 ? base.legend : { show: false },
    dataZoom: zoom.dataZoom,
  };
});

const height = computed(() =>
  resolveChartHeight("line", props.spec.height),
);

function onClick(p: { dataIndex: number }) {
  if (props.spec.id && p && typeof p.dataIndex === "number") {
    emit(
      "click-target",
      props.spec.id,
      rows.value[p.dataIndex] as Record<string, unknown>,
    );
  }
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
